import React, { useEffect, useMemo, useState } from 'react';
import { Button, InputNumber, Modal, Popover, Space, Tooltip } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { saveCompanyBudgetYear, type CompanyBudgetWorkspaceDto } from './companyBudgetApi';
import {
  buildPlanGrid,
  changedMonthsPayload,
  copyFirstMonth,
  invalidCells,
  MAX_PLAN_AMOUNT,
  planPreview,
  planRows,
  rowTotal,
  spreadEvenly,
  type PlanGrid,
  type PlanRow,
  type PlanValues,
} from './budgetPlan';
import { useFeedback } from '../../../utils/feedback';
import { formatCurrency } from '../../../utils/currency';
import { formatLocalizedNumber } from '../../../utils/numberFormatting';
import './BudgetPlanEditor.css';

// „Plan roku” (replaces „Ustaw cele”, decision 2026-10-29-grupy-kosztow-i-budzet):
// the year × 12 months grid of revenue, margin, fixed costs in each cost group
// and „Bez grupy”, and the entered net profit plan. Fixed costs in total, EBITDA
// and the profit computed from the plan are derived rows. Only the months the
// user changed are saved (one transaction, PUT /company-budgets/years/:year).

interface Props {
  open: boolean;
  year: number;
  budget: CompanyBudgetWorkspaceDto | null;
  monthLabels: string[];
  onClose: () => void;
  onSaved: () => void;
}

// Thousands grouped like every money display („6 000 000”); the parser accepts
// spaces and a decimal comma.
const formatter = (value: number | string | undefined) => {
  if (value === undefined || value === '' || value === null) return '';
  const [whole, fraction] = String(value).split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}${fraction ? `,${fraction}` : ''}`;
};
const parser = (value: string | undefined) => (value ?? '').replace(/\s/g, '').replace(',', '.');

// Cells show whole złoty (the grid has 14 columns); the exact amount is in `title`.
const amount = (value: number | null) => (value == null ? '' : formatLocalizedNumber(value, 0, 'pl'));
const exact = (value: number | null) => (value == null ? undefined : formatCurrency(value, 'PLN'));

const QuickEntry: React.FC<{ label: string; onApply: (values: PlanValues) => void; values: PlanValues }> = ({ label, onApply, values }) => {
  const { t } = useTranslation(['reports']);
  const [open, setOpen] = useState(false);
  const [annual, setAnnual] = useState<number | null>(null);
  const apply = (next: PlanValues) => { onApply(next); setOpen(false); };
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      title={t('reports:budgetPlan.quickTitle', { row: label })}
      content={(
        <div className="budget-plan__quick">
          <div className="budget-plan__quick-row">
            <InputNumber
              value={annual}
              onChange={(value) => setAnnual(value == null ? null : Number(value))}
              formatter={formatter}
              parser={parser}
              controls={false}
              addonAfter="zł"
              placeholder={t('reports:budgetPlan.annualPlaceholder')}
              aria-label={t('reports:budgetPlan.annualPlaceholder')}
              data-testid="budget-plan-annual"
            />
            <Button type="primary" disabled={annual == null} onClick={() => annual != null && apply(spreadEvenly(annual))} data-testid="budget-plan-spread">
              {t('reports:budgetPlan.spread')}
            </Button>
          </div>
          <Space>
            <Button size="small" disabled={values.every((value) => value == null)} onClick={() => apply(copyFirstMonth(values))} data-testid="budget-plan-copy">
              {t('reports:budgetPlan.copyFirst')}
            </Button>
            <Button size="small" onClick={() => apply(values.map(() => null))}>{t('reports:budgetPlan.clearRow')}</Button>
          </Space>
        </div>
      )}
    >
      <Tooltip title={t('reports:budgetPlan.quick')}>
        <Button type="text" size="small" icon={<ThunderboltOutlined />} aria-label={t('reports:budgetPlan.quickTitle', { row: label })} data-testid="budget-plan-quick" />
      </Tooltip>
    </Popover>
  );
};

const BudgetPlanEditor: React.FC<Props> = ({ open, year, budget, monthLabels, onClose, onSaved }) => {
  const { t } = useTranslation(['reports']);
  const feedback = useFeedback();
  const rows = useMemo(() => planRows(budget), [budget]);
  const [initial, setInitial] = useState<PlanGrid>({});
  const [grid, setGrid] = useState<PlanGrid>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const seeded = buildPlanGrid(budget, year, rows);
    setInitial(seeded);
    setGrid(seeded);
  }, [open, budget, year, rows]);

  const preview = useMemo(() => planPreview(grid, rows), [grid, rows]);
  const invalid = useMemo(() => invalidCells(grid, rows), [grid, rows]);
  const hasGroups = rows.some((row) => row.kind === 'group');

  const labelOf = (row: PlanRow) => {
    if (row.kind === 'group' && row.group) {
      return row.group.isActive ? row.group.name : t('reports:budgetPlan.archivedGroup', { name: row.group.name });
    }
    return t(`reports:budgetPlan.rows.${row.kind}`);
  };

  const setCell = (key: string, index: number, value: number | null) => setGrid((current) => {
    const values = [...(current[key] ?? Array(12).fill(null))];
    values[index] = value;
    return { ...current, [key]: values };
  });
  const setRow = (key: string, values: PlanValues) => setGrid((current) => ({ ...current, [key]: values }));

  async function save() {
    if (invalid.size > 0) {
      feedback.error(t('reports:budgetPlan.invalid', { max: formatCurrency(MAX_PLAN_AMOUNT, 'PLN') }));
      return;
    }
    const months = changedMonthsPayload(year, rows, initial, grid);
    if (months.length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await saveCompanyBudgetYear(year, months);
      feedback.success(t('reports:budgetPlan.saved', { year, count: months.length }));
      onSaved();
      onClose();
    } catch {
      feedback.error(t('reports:budgetPlan.saveFailed', { year }));
    } finally {
      setSaving(false);
    }
  }

  const inputRow = (row: PlanRow) => {
    const values = grid[row.key] ?? Array(12).fill(null);
    const label = labelOf(row);
    return (
      <tr key={row.key} className={`budget-plan__row budget-plan__row--${row.kind}`} data-testid={`budget-plan-row-${row.kind}`}>
        <th scope="row" className="budget-plan__label"><span className="report-cut" title={label}>{label}</span></th>
        {values.map((value, index) => (
          <td key={monthLabels[index] ?? index}>
            <InputNumber
              className="budget-plan__input"
              value={value}
              onChange={(next) => setCell(row.key, index, next == null || next === '' ? null : Number(next))}
              formatter={formatter}
              parser={parser}
              controls={false}
              status={invalid.has(`${row.key}:${index}`) ? 'error' : undefined}
              aria-label={`${label} ${monthLabels[index] ?? ''}`}
            />
          </td>
        ))}
        <td className="budget-plan__total" title={exact(rowTotal(values))}>{amount(rowTotal(values))}</td>
        <td className="budget-plan__action"><QuickEntry label={label} values={values} onApply={(next) => setRow(row.key, next)} /></td>
      </tr>
    );
  };

  const derivedRow = (key: 'fixedCosts' | 'ebitda' | 'profitCalculated', hint?: string) => {
    const values = preview.map((month) => month[key]);
    const label = t(`reports:budgetPlan.rows.${key}`);
    return (
      <tr key={key} className={`budget-plan__row budget-plan__row--derived budget-plan__row--${key}`} data-testid={`budget-plan-row-${key}`}>
        <th scope="row" className="budget-plan__label">
          <span className="report-cut" title={hint ? `${label} — ${hint}` : label}>{label}</span>
        </th>
        {values.map((value, index) => (
          <td key={monthLabels[index] ?? index} title={exact(value)} className={`budget-plan__value ${value != null && value < 0 ? 'report-negative' : ''}`}>{amount(value)}</td>
        ))}
        <td className="budget-plan__total" title={exact(rowTotal(values))}>{amount(rowTotal(values))}</td>
        <td />
      </tr>
    );
  };

  const costRows = rows.filter((row) => row.kind === 'group' || row.kind === 'ungrouped');
  const byKind = (kind: PlanRow['kind']) => rows.filter((row) => row.kind === kind);

  return (
    <Modal
      open={open}
      title={t('reports:budgetPlan.title', { year })}
      onCancel={() => !saving && onClose()}
      width="min(1400px, calc(100vw - 32px))"
      className="budget-plan-modal"
      style={{ top: 24 }}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>{t('reports:budgetPlan.cancel')}</Button>,
        <Button key="save" type="primary" loading={saving} onClick={() => void save()} data-testid="mgmt-targets-save">{t('reports:budgetPlan.save')}</Button>,
      ]}
    >
      <p className="budget-plan__hint" title={t('reports:budgetPlan.hintFull')}>{t('reports:budgetPlan.hint')}</p>
      <div className="budget-plan__scroll">
        <table className="budget-plan" data-testid="budget-plan">
          <thead>
            <tr>
              <th className="budget-plan__label">{t('reports:budgetPlan.item')}</th>
              {monthLabels.map((label) => <th key={label}>{label}</th>)}
              <th className="budget-plan__total">{t('reports:budgetPlan.year')}</th>
              <th className="budget-plan__action" aria-label={t('reports:budgetPlan.quick')} />
            </tr>
          </thead>
          <tbody>
            {byKind('revenue').map(inputRow)}
            {byKind('margin').map(inputRow)}
            <tr className="budget-plan__section"><th colSpan={15}>{t('reports:budgetPlan.costsSection')}</th></tr>
            {costRows.map(inputRow)}
            {derivedRow('fixedCosts')}
            {derivedRow('ebitda', t('reports:budgetPlan.ebitdaHint'))}
            <tr className="budget-plan__section"><th colSpan={15}>{t('reports:budgetPlan.profitSection')}</th></tr>
            {byKind('profit').map(inputRow)}
            {derivedRow('profitCalculated', t('reports:budgetPlan.profitCalculatedHint'))}
          </tbody>
        </table>
      </div>
      {!hasGroups && <p className="budget-plan__hint">{t('reports:budgetPlan.noGroups')}</p>}
    </Modal>
  );
};

export default BudgetPlanEditor;
